import { useCallback, useRef, useState } from "react";
import type { PlanEvent } from "../../../shared/types";
import {
  minutesToTime,
  snapToPx,
  pxToMinutes,
  type DraggingState,
  type ResizingState,
  type UpdateFn,
  calculateDragDuration,
  calculateResizeDelta,
  resolveDragTarget,
  clampTopPx,
} from "./drag-drop-helpers";
import { timeToMinutes, getEventPosition } from "../utils/time-positioning";

export function useDragDrop(
  days: readonly string[],
  onUpdate: UpdateFn | undefined,
) {
  const [dragging, setDragging] = useState<DraggingState | null>(null);
  const [resizing, setResizing] = useState<ResizingState | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const initialTopPxRef = useRef(0);
  const initialMouseYRef = useRef(0);

  const startDrag = useCallback((event: PlanEvent, mouseYInColumn: number) => {
    const { topPx, durationMinutes } = calculateDragDuration(event);
    initialTopPxRef.current = topPx;
    initialMouseYRef.current = mouseYInColumn;
    setDragging({
      event,
      ghostDate: event.date,
      ghostTopPx: topPx,
      durationMinutes,
    });
    return { offsetMinutes: mouseYInColumn - topPx };
  }, []);

  const updateDrag = useCallback(
    (clientX: number, clientY: number, freeSnap = false) => {
      const { ghostDate, gridY } = resolveDragTarget(
        clientX,
        clientY,
        days,
        gridRef,
      );

      if (gridY === null) {
        setDragging((prev) => (prev ? { ...prev, ghostDate } : null));
        return;
      }

      const delta = gridY - initialMouseYRef.current;
      const rawTopPx = initialTopPxRef.current + delta;
      const snappedTopPx = snapToPx(rawTopPx, freeSnap);
      const clampedTopPx = clampTopPx(snappedTopPx);

      setDragging((prev) =>
        prev ? { ...prev, ghostDate, ghostTopPx: clampedTopPx, freeSnap } : null,
      );
    },
    [days],
  );

  const commitDrag = useCallback(() => {
    if (!dragging || !onUpdate) return;
    const snap = dragging.freeSnap ? 1 : 15;
    const startMinutes = Math.round(pxToMinutes(dragging.ghostTopPx) / snap) * snap;
    const endMinutes = startMinutes + dragging.durationMinutes;
    onUpdate(dragging.event.id, {
      date: dragging.ghostDate,
      startTime: minutesToTime(startMinutes),
      endTime: minutesToTime(endMinutes),
    });
    setDragging(null);
  }, [dragging, onUpdate]);

  const cancelDrag = useCallback(() => setDragging(null), []);

  const startResize = useCallback(
    (event: PlanEvent, edge: "top" | "bottom") => {
      const { topPx, heightPx } = getEventPosition(
        event.startTime,
        event.endTime,
      );
      setResizing({
        event,
        edge,
        ghostTopPx: topPx,
        ghostHeightPx: heightPx,
        newStartMinutes: timeToMinutes(event.startTime),
        newEndMinutes: timeToMinutes(event.endTime),
      });
    },
    [],
  );

  const updateResize = useCallback((deltaY: number, freeSnap = false) => {
    setResizing((prev) => {
      if (!prev) return null;
      const delta = calculateResizeDelta(deltaY, prev.edge, prev, freeSnap);
      return { ...prev, ...delta };
    });
  }, []);

  const commitResize = useCallback(() => {
    if (!resizing || !onUpdate) return;
    onUpdate(resizing.event.id, {
      startTime: minutesToTime(resizing.newStartMinutes),
      endTime: minutesToTime(resizing.newEndMinutes),
    });
    setResizing(null);
  }, [resizing, onUpdate]);

  const cancelResize = useCallback(() => setResizing(null), []);

  return {
    gridRef,
    dragging,
    resizing,
    startDrag,
    updateDrag,
    commitDrag,
    cancelDrag,
    startResize,
    updateResize,
    commitResize,
    cancelResize,
  };
}

export type { DraggingState, ResizingState };
